// Styles
import './VSelectionControlGroup.sass'

// Composables
import { makeComponentProps } from '@/composables/component'
import { provideDefaults } from '@/composables/defaults'
import { makeDensityProps } from '@/composables/density'
import { IconValue } from '@/composables/icons'
import { useProxiedModel } from '@/composables/proxiedModel'
import { makeThemeProps } from '@/composables/theme'

// Utilities
import { onScopeDispose, provide, toRef, useId } from 'vue'
import { deepEqual, genericComponent, getActiveElement, IN_BROWSER, propsFactory, useRender } from '@/util'

// Types
import type { InjectionKey, PropType, Ref } from 'vue'
import type { RippleDirectiveBinding } from '@/directives/ripple'
import type { GenericProps, ValueComparator } from '@/util'

export interface VSelectionGroupContext {
  modelValue: Ref<any>
  forceUpdate: () => void
  onForceUpdate: (fn: () => void) => void
  register: (control: VSelectionControlInstance) => void
}

export interface VSelectionControlInstance {
  el: () => HTMLInputElement | undefined
  focus: (options?: FocusOptions) => void
  isChecked: () => boolean
}

export const VSelectionControlGroupSymbol: InjectionKey<VSelectionGroupContext> = Symbol.for('vuetify:selection-control-group')

export const makeSelectionControlGroupProps = propsFactory({
  color: String,
  disabled: {
    type: Boolean as PropType<boolean | null>,
    default: null,
  },
  defaultsTarget: String,
  error: Boolean,
  id: String,
  inline: Boolean,
  falseIcon: IconValue,
  trueIcon: IconValue,
  indeterminateIcon: IconValue,
  ripple: {
    type: [Boolean, Object] as PropType<RippleDirectiveBinding['value']>,
    default: true,
  },
  multiple: {
    type: Boolean as PropType<boolean | null>,
    default: null,
  },
  name: String,
  readonly: {
    type: Boolean as PropType<boolean | null>,
    default: null,
  },
  modelValue: null,
  type: String,
  valueComparator: {
    type: Function as PropType<ValueComparator>,
    default: deepEqual,
  },

  ...makeComponentProps(),
  ...makeDensityProps(),
  ...makeThemeProps(),
}, 'SelectionControlGroup')

export const makeVSelectionControlGroupProps = propsFactory({
  ...makeSelectionControlGroupProps({
    defaultsTarget: 'VSelectionControl',
  }),
}, 'VSelectionControlGroup')

export const VSelectionControlGroup = genericComponent<new <T>(
  props: {
    modelValue?: T
    'onUpdate:modelValue'?: (value: T) => void
  },
  slots: { default: never },
) => GenericProps<typeof props, typeof slots>>()({
  name: 'VSelectionControlGroup',

  props: makeVSelectionControlGroupProps(),

  emits: {
    'update:modelValue': (value: any) => true,
  },

  setup (props, { slots }) {
    const modelValue = useProxiedModel(props, 'modelValue')
    const uid = useId()
    const id = toRef(() => props.id || `v-selection-control-group-${uid}`)
    const name = toRef(() => props.name || id.value)

    const updateHandlers = new Set<() => void>()
    const controls = new Set<VSelectionControlInstance>()

    provide(VSelectionControlGroupSymbol, {
      modelValue,
      forceUpdate: () => {
        updateHandlers.forEach(fn => fn())
      },
      onForceUpdate: cb => {
        updateHandlers.add(cb)
        onScopeDispose(() => {
          updateHandlers.delete(cb)
        })
      },
      register: control => {
        if (control) {
          controls.add(control)
          onScopeDispose(() => {
            controls.delete(control)
          })
        }
      },
    })

    function focus (options?: FocusOptions) {
      if (!IN_BROWSER) return

      // Find the first checked control or the first enabled one, in DOM order
      let first: VSelectionControlInstance | undefined
      for (const control of controls) {
        const el = control.el()
        if (!el || el.disabled) continue

        if (!first) {
          first = control
          continue
        }

        const checked = control.isChecked()
        const firstEl = first.el()
        if (
          (checked && !first.isChecked()) ||
          (checked === first.isChecked() && firstEl && (firstEl.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_PRECEDING))
        ) {
          first = control
        }
      }

      first?.focus(options)
    }

    function blur () {
      if (!IN_BROWSER) return

      const activeEl = getActiveElement()
      if (!activeEl) return

      for (const c of controls) {
        const el = c.el()
        if (el && el === activeEl) {
          el.blur()
          return
        }
      }
    }

    provideDefaults({
      [props.defaultsTarget]: {
        color: toRef(() => props.color),
        disabled: toRef(() => props.disabled),
        density: toRef(() => props.density),
        error: toRef(() => props.error),
        inline: toRef(() => props.inline),
        modelValue,
        multiple: toRef(() => !!props.multiple || (props.multiple == null && Array.isArray(modelValue.value))),
        name,
        falseIcon: toRef(() => props.falseIcon),
        trueIcon: toRef(() => props.trueIcon),
        readonly: toRef(() => props.readonly),
        ripple: toRef(() => props.ripple),
        type: toRef(() => props.type),
        valueComparator: toRef(() => props.valueComparator),
      },
    })

    useRender(() => (
      <div
        class={[
          'v-selection-control-group',
          { 'v-selection-control-group--inline': props.inline },
          props.class,
        ]}
        style={ props.style }
        role={ props.type === 'radio' ? 'radiogroup' : undefined }
      >
        { slots.default?.() }
      </div>
    ))

    return { blur, focus }
  },
})

export type VSelectionControlGroup = InstanceType<typeof VSelectionControlGroup>
